<script setup lang="ts">
import type { CardCarouselImageProps } from '.';

import { cn } from '@/utils';
import { useLazyLoadImage } from '~/composables/useLazyLoadImage';

const props = defineProps<CardCarouselImageProps>();
const imageRef =
	useTemplateRef<ComponentPublicInstance<HTMLImageElement>>('image-ref');

const IS_BIGGER_OR_EQUAL_THREE = props.index >= 3;

const imageUrl = (
	'watermarkUrl' in props.image
		? (props.image.watermarkUrl ?? props.image.url)
		: props.image.url
)!;
const { lazilyLoadImage } = useLazyLoadImage(0.1, imageUrl);
const imageSrc = computed(() =>
	!IS_BIGGER_OR_EQUAL_THREE ? imageUrl : undefined
);

const width = computed(() => (props.variant === 'fullFace' ? 960 : 700));
const placeholder = computed(
	() =>
		(IS_BIGGER_OR_EQUAL_THREE && [50, 25, 75, 5]) as
			| [number, number, number, number]
			| false
);
// Quality 85 oferece ótimo equilíbrio entre qualidade visual e tamanho do arquivo
const quality = computed(() => (props.variant === 'fullFace' ? 85 : 80));

onMounted(() => {
	if (!IS_BIGGER_OR_EQUAL_THREE) return;

	lazilyLoadImage(imageRef.value?.$el);
});
</script>

<template>
	<div
		class="relative flex size-full items-center justify-center"
		data-testid="carousel-image-wrapper"
		:class="
			cn(
				!props.isPropertyActive &&
					'before:content-[\'\'] before:absolute before:inset-0 before:bg-white/40 before:pointer-events-none before:z-5'
			)
		"
	>
		<NuxtImg
			ref="image-ref"
			format="webp"
			class="size-full object-cover"
			:width="width"
			height="400"
			sizes="xs:375px sm:320px md:384px lg:480px xl:583px"
			densities="x1"
			:src="imageSrc"
			:alt="imageAlt"
			:loading="index === 0 ? 'eager' : 'lazy'"
			:quality="quality"
			:placeholder="placeholder"
		/>
	</div>
</template>
